import React, { useCallback, useEffect, useRef, useState } from "react";
import logo from "./images/KrossKast_Logo-red.png";
import backgroundImage from "./images/sign-up-desk-background-image.jpg";
import "./App.css";
import "./signin.css";
import "./PasswordChanged.css";
import authService from "./services/authService";
import {
  PASSWORD_CHANGED_MODE_SUCCESS,
  resolvePasswordChangedMode,
  stripPasswordChangedEvidence,
} from "./auth/passwordChangedEvidence";

export const SUCCESS_REDIRECT_SECONDS = 15;
export const NEUTRAL_REDIRECT_SECONDS = 5;

export function maSignInPath() {
  return "/signin";
}

/**
 * Lands here after Auth0 password-change (New UL Post Challenge) and for
 * generic Auth0 fallbacks to the Application Login URI. No tokens in the URL;
 * does not auto-login.
 *
 * Security contract (every arrival, independent of copy): wipe any
 * pre-existing MA SPA identity, then continue into a forced interactive
 * Auth0 login (prompt=login). A prior User A session must never become the
 * post-reset application identity.
 *
 * Copy: "Password Changed!" only with fresh display evidence from the Post
 * Challenge Action; otherwise neutral "Please sign in again".
 *
 * Visual shell matches ForgotPassword / ConfirmEmail (desk background + card).
 */
export default function PasswordChanged() {
  const [mode] = useState(() =>
    resolvePasswordChangedMode(
      typeof window !== "undefined" ? window.location.search : "",
    ),
  );
  const isSuccess = mode === PASSWORD_CHANGED_MODE_SUCCESS;
  const [secondsLeft, setSecondsLeft] = useState(
    isSuccess ? SUCCESS_REDIRECT_SECONDS : NEUTRAL_REDIRECT_SECONDS,
  );
  const [continueError, setContinueError] = useState(null);
  const [continuing, setContinuing] = useState(false);
  const continueStartedRef = useRef(false);

  useEffect(() => {
    // Fail closed immediately: drop prior MA identity + Auth0 token cache
    // while the branded confirmation is visible. Do this only here / in
    // continueAfterPasswordChange — never from isLoggedIn during /callback.
    try {
      authService.invalidatePriorIdentityForFreshLogin();
    } catch {
      /* ignore — continue path still forces prompt=login */
    }
    stripPasswordChangedEvidence();
  }, []);

  const continueToFreshLogin = useCallback(async () => {
    if (continueStartedRef.current) return;
    continueStartedRef.current = true;
    setContinuing(true);
    setContinueError(null);
    try {
      const result = await authService.continueAfterPasswordChange();
      if (result && result.success === false) {
        setContinueError(result.error || "Unable to start sign-in");
        setContinuing(false);
        continueStartedRef.current = false;
      }
      // On success Auth0 redirects away — leave continuing=true.
    } catch (err) {
      setContinueError(err?.message || "Unable to start sign-in");
      setContinuing(false);
      continueStartedRef.current = false;
    }
  }, []);

  useEffect(() => {
    const tick = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(tick);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => {
    if (secondsLeft !== 0) return undefined;
    void continueToFreshLogin();
    return undefined;
  }, [secondsLeft, continueToFreshLogin]);

  const goNow = () => {
    void continueToFreshLogin();
  };

  return (
    <div
      className="signin-page kk-password-changed"
      data-testid="password-changed-page"
      data-mode={mode}
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
      }}
    >
      <div className="top-nav">
        <img src={logo} alt="KrossKast" className="nav-logo" />
      </div>
      <div className="signin-container">
        <div
          className="signin-form-container kk-password-changed__card"
          style={{ maxWidth: 400, textAlign: "center" }}
        >
          <img
            src={logo}
            alt="KrossKast"
            className="kk-password-changed__logo"
          />
          {isSuccess ? (
            <>
              <h1>Password Changed!</h1>
              <p>Your password has been changed successfully.</p>
            </>
          ) : (
            <>
              <h1>Please sign in again</h1>
              <p>
                Your previous sign-in session is no longer available. Please
                sign in to continue.
              </p>
            </>
          )}
          <p className="kk-password-changed__redirect">
            You will be automatically redirected to the KrossKast login page in{" "}
            <strong data-testid="password-changed-countdown">
              {secondsLeft}
            </strong>{" "}
            seconds.
          </p>
          {continueError ? (
            <p data-testid="password-changed-continue-error">{continueError}</p>
          ) : null}
          <button
            type="button"
            className="continue-btn kk-password-changed__cta"
            onClick={goNow}
            disabled={continuing}
            data-testid="password-changed-go-login"
          >
            {continuing
              ? "Redirecting…"
              : isSuccess
                ? "Go to Login Now"
                : "Go to Login"}
          </button>
        </div>
      </div>
    </div>
  );
}
